import { IBaseResourceModel, IFileAttachmentWithURL } from '.';
import { IRawRichTextContentState } from '../../../view/models/Api';

export enum CustomPageType {
	BlogHome = 'BlogHome',
	BlogIndividualPage = 'BlogIndividualPage',
	BlogPost = 'BlogPost',
	BlogPreviewPage = 'BlogPreviewPage',
	Footer = 'Footer',
	General = 'General',
	Header = 'Header',
	Meeting = 'Meeting',
	Survey = 'Survey',
}

/**
 * Blog-related page types used for the "Blogs" feature toggle.
 * When blogs are enabled, these page types are included in enabledFor.
 */
export const BLOG_PAGE_TYPES: CustomPageType[] = [
	CustomPageType.BlogHome,
	CustomPageType.BlogIndividualPage,
	CustomPageType.BlogPost,
	CustomPageType.BlogPreviewPage,
];

export interface CustomPageStyle extends IBaseResourceModel {
	type: CustomPageType;
	css?: string;
}
export interface FooterCustomization extends CustomPageStyle {
	backgroundColor?: string;
	textColor?: string;
	linkColor?: string;
	text?: IRawRichTextContentState;
	copyrightInfo?: string;
	links?: NavLink[];
	type: CustomPageType.Footer;
	/** Page types where this footer style is applied. If null/empty, applied to all. */
	enabledFor?: CustomPageType[];
}
export interface HeaderCustomization extends CustomPageStyle {
	backgroundColor?: string;
	dropdown: TextAttributes;
	hoverColor?: string;
	logoOnlyHeader?: boolean;
	menuLinks?: NavLink[];
	overallFont?: BlogFont;
	textColor?: string;
	mainPageTitle?: string;
	type: CustomPageType.Header;
	/** Page types where this header style is applied. If null/empty, applied to all. */
	enabledFor?: CustomPageType[];
}

export interface BlogHomeCustomization extends CustomPageStyle {
	pageBackgroundColor?: string;
	headerImage?: IFileAttachmentWithURL;
	hideNook?: boolean;
	font?: BlogFont;
	blogTitle: CustomTitleText;
	cardStyle: BlogCard;
	type: CustomPageType.BlogHome;
}
export interface BlogPostCustomization extends CustomPageStyle {
	blogTitle?: TextAttributes;
	blogHeadlines?: TextAttributes;
	blogBody?: TextAttributes;
	showMoreButton?: PageButton;
	type: CustomPageType.BlogPost;
}
export interface NavLink {
	label?: string;
	destination?: string;
	children?: NavLink[];
}
export interface FontSelection {
	fontName?: string;
	fontUri?: string;
}
export type BlogFont = FontSelection;
export interface TextAttributes {
	backgroundColor: string;
	font: BlogFont;
	color: string;
	fontSizePx?: number;
	mobileFontSizePx?: number;
}
export interface PageButton extends TextAttributes {
	hoverColor: string;
}

export interface CustomTitleText extends TextAttributes {
	title: string;
}

interface BlogCard {
	titleStyle?: TextAttributes;
	linkStyle?: TextAttributes;
	showAuthorMetaData?: boolean;
}

export type CustomBlogStyles =
	| HeaderCustomization
	| FooterCustomization
	| BlogHomeCustomization
	| BlogPostCustomization;

/**
 * Configuration for overriding account-level branding at the individual survey or meeting level.
 *
 * This allows surveys and meetings to have their own custom header/footer branding
 * independent of the account's default branding.
 *
 * Fallback chain:
 * 1. Individual survey/meeting customization (if specified)
 * 2. Account-level customization
 * 3. System default (no customization)
 *
 * @property headerStyleId - Optional reference to a specific header CustomPageStyle ID
 * @property footerStyleId - Optional reference to a specific footer CustomPageStyle ID
 * @property useAccountDefault - If true, use account-level branding; if false, use individual customization
 *
 * Backend API Requirements:
 * - New endpoint: GET /survey/{id}/custom-page-style/{type}
 * - New endpoint: PUT /survey/{id}/custom-page-style/{type}
 * - New endpoint: DELETE /survey/{id}/custom-page-style/{type}
 * - Similar endpoints for meetings: /scheduler/{shortCode}/custom-page-style/{type}
 * - Backend must implement fallback logic when override is not found
 */
export interface CustomPageStyleOverride {
	/**
	 * Optional reference to a specific header CustomPageStyle.
	 * If provided, this header will be used instead of the account default.
	 */
	headerStyleId?: string;

	/**
	 * Optional reference to a specific footer CustomPageStyle.
	 * If provided, this footer will be used instead of the account default.
	 */
	footerStyleId?: string;

	/**
	 * Determines whether to use account-level branding or individual customization.
	 * - true: Use account-level default branding
	 * - false: Use individual survey/meeting customization specified by styleIds
	 *
	 * Default: true (use account branding for backward compatibility)
	 */
	useAccountDefault: boolean;
}
